import api from './api';

/**
 * Stripe Payment Service
 *
 * Handles all Stripe PaymentIntent-related API calls to the backend
 */
class StripeService {
  /**
   * Create a PaymentIntent for an order
   *
   * @param {number} orderId - The order ID
   * @returns {Promise<Object>} Payment intent response
   * @throws {Error} If payment intent creation fails
   *
   * Response format:
   * {
   *   paymentId: string,      // Stripe PaymentIntent ID (e.g., "pi_123abc")
   *   clientSecret: string,   // Secret for frontend confirmation
   *   status: string          // Payment status (e.g., "requires_payment_method")
   * }
   */
  async createPaymentIntent(orderId) {
    try {
      const response = await api.post(`/orders/${orderId}/payment/stripe`);
      // Backend returns ApiResponse wrapper, extract the actual data
      return response.data.data;
    } catch (error) {
      console.error('[Stripe Service] Failed to create payment intent:', error);
      throw this._handleError(error);
    }
  }

  /**
   * Get payment status for a specific payment intent
   *
   * @param {number} orderId - The order ID
   * @param {string} paymentIntentId - The Stripe PaymentIntent ID
   * @returns {Promise<Object>} Payment status response
   * @throws {Error} If status check fails
   *
   * Response format:
   * {
   *   status: string  // Current payment status
   * }
   */
  async getPaymentStatus(orderId, paymentIntentId) {
    try {
      const response = await api.get(
        `/orders/${orderId}/payment/${paymentIntentId}/status`
      );
      // Backend returns ApiResponse wrapper, extract the actual data
      return response.data.data;
    } catch (error) {
      console.error('[Stripe Service] Failed to get payment status:', error);
      throw this._handleError(error);
    }
  }

  /**
   * Cancel a pending payment intent
   *
   * @param {number} orderId - The order ID
   * @param {string} paymentIntentId - The Stripe PaymentIntent ID
   * @returns {Promise<void>}
   * @throws {Error} If cancellation fails
   */
  async cancelPayment(orderId, paymentIntentId) {
    try {
      await api.post(`/orders/${orderId}/payment/${paymentIntentId}/cancel`);
    } catch (error) {
      console.error('[Stripe Service] Failed to cancel payment:', error);
      throw this._handleError(error);
    }
  }

  /**
   * Poll order status until payment is confirmed or timeout
   *
   * This is useful for handling webhook delays - the frontend can poll
   * the order status to confirm payment completion.
   *
   * @param {number} orderId - The order ID to poll
   * @param {Object} options - Polling configuration
   * @param {number} [options.maxAttempts=30] - Maximum polling attempts
   * @param {number} [options.interval=2000] - Interval between attempts in ms
   * @returns {Promise<Object>} Result object with success flag and order data
   * @throws {Error} If polling fails or times out
   *
   * Return format:
   * {
   *   success: boolean,     // True if payment succeeded
   *   order: Object,        // Order object from API
   *   error: string|null    // Error message if failed
   * }
   */
  async pollOrderStatus(orderId, { maxAttempts = 30, interval = 2000 } = {}) {
    console.log(
      `[Stripe Service] Starting to poll order ${orderId} status ` +
      `(max ${maxAttempts} attempts, ${interval}ms interval)`
    );

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const response = await api.get(`/orders/${orderId}`);
        const order = response.data;

        console.log(
          `[Stripe Service] Poll attempt ${attempt}/${maxAttempts}: ` +
          `status=${order.paymentStatus}`
        );

        // Payment succeeded
        if (order.paymentStatus === 'PAID') {
          console.log('[Stripe Service] Payment confirmed as PAID');
          return { success: true, order, error: null };
        }

        // Payment failed
        if (order.paymentStatus === 'FAILED') {
          console.log('[Stripe Service] Payment failed');
          return {
            success: false,
            order,
            error: 'Payment failed'
          };
        }

        // Payment cancelled
        if (order.paymentStatus === 'CANCELLED') {
          console.log('[Stripe Service] Payment cancelled');
          return {
            success: false,
            order,
            error: 'Payment cancelled'
          };
        }

        // Still pending, continue polling if not last attempt
        if (attempt < maxAttempts) {
          await this._delay(interval);
        }
      } catch (error) {
        console.error(
          `[Stripe Service] Poll attempt ${attempt} failed:`,
          error.message
        );

        // If not the last attempt, continue polling
        if (attempt < maxAttempts) {
          await this._delay(interval);
        } else {
          throw new Error('Failed to confirm payment status');
        }
      }
    }

    // Timeout
    const timeoutMessage =
      'Payment status check timeout. Please check your order status.';
    console.error('[Stripe Service]', timeoutMessage);
    throw new Error(timeoutMessage);
  }

  /**
   * Helper to delay execution
   * @private
   * @param {number} ms - Milliseconds to delay
   * @returns {Promise<void>}
   */
  _delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Handle API errors and convert to user-friendly messages
   * @private
   * @param {Error} error - The error object from axios
   * @returns {Error} A new error with user-friendly message
   */
  _handleError(error) {
    if (error.response) {
      const { status, data } = error.response;

      switch (status) {
        case 400:
          return new Error(data.message || 'Invalid request');

        case 401:
          return new Error('Authentication required. Please log in.');

        case 403:
          return new Error('You do not have permission to perform this action.');

        case 404:
          return new Error('Order not found');

        case 409:
          return new Error('Order has already been paid');

        case 500:
          return new Error('Server error. Please try again later.');

        default:
          return new Error(data.message || 'An error occurred');
      }
    }

    if (error.request) {
      return new Error('Network error. Please check your connection.');
    }

    return error;
  }
}

// Export singleton instance
export const stripeService = new StripeService();
export default stripeService;
